import { apiClient } from '@/lib/api';
import axiosInstance from '@/lib/api/axios-instance';
import { ENDPOINTS } from '@/lib/api/endpoints';
import type { PersonaSinRestaurar } from '../components/aviso-de-restauracion';
import type {
  DestinoDeSoporte,
  AdminStats,
  AdminTenant,
  ComparacionDeEmpresas,
  AdminUser,
  DetalleDeUsuario,
  ModulosDeEmpresa,
  PermisosDeEmpresa,
  FiltroDeDemo,
  OpcionesDeLaDemo,
  Pagination,
  ResultadoDelCambioDeEje,
  ResultadoDeLaConversion,
  RevisionDeBorrado,
  RevisionDeReinicio,
  ResultadoDelReinicio,
  ResumenDelClon,
  InvitacionDemo,
  AltaDeInvitacionDemo,
} from '../types/admin.types';

class AdminService {
  async getStats(): Promise<AdminStats> {
    return apiClient.get<AdminStats>(ENDPOINTS.ADMIN_STATS);
  }

  /** Todas las empresas con sus métricas, para compararlas. */
  async getComparacion(incluirVacias = false): Promise<ComparacionDeEmpresas> {
    const url = incluirVacias
      ? `${ENDPOINTS.ADMIN_COMPARACION}?incluirVacias=true`
      : ENDPOINTS.ADMIN_COMPARACION;
    return apiClient.get<ComparacionDeEmpresas>(url);
  }

  async getTenants(params?: {
    search?: string;
    status?: string;
    /** Sólo demos: las que están listas para la venta, abandonadas o con el rubro sin tiendas. */
    demo?: FiltroDeDemo;
    page?: number;
    limit?: number;
  }): Promise<{ data: AdminTenant[]; pagination: Pagination }> {
    const query = new URLSearchParams();
    if (params?.search) query.set('search', params.search);
    if (params?.status) query.set('status', params.status);
    if (params?.demo) query.set('demo', params.demo);
    if (params?.page) query.set('page', String(params.page));
    if (params?.limit) query.set('limit', String(params.limit));
    const url = `${ENDPOINTS.ADMIN_TENANTS}?${query.toString()}`;
    const response = await axiosInstance.get(url);
    return { data: response.data.data, pagination: response.data.pagination };
  }

  /** Reglas de la demo (343): los rubros y tiendas que se pueden elegir y el estado de una empresa en prueba. */
  async getOpcionesDeLaDemo(id: string): Promise<OpcionesDeLaDemo> {
    return apiClient.get<OpcionesDeLaDemo>(ENDPOINTS.ADMIN_TENANT_DEMO(id));
  }

  /** «Cambiar rubro y tienda»: con tienda, el mismo cambio que hace el comercio (nada se borra). */
  async cambiarEjeDeLaDemo(
    id: string,
    datos: { ejeId: string; sitio?: string }
  ): Promise<ResultadoDelCambioDeEje> {
    return apiClient.post<ResultadoDelCambioDeEje>(
      ENDPOINTS.ADMIN_TENANT_DEMO_CAMBIAR_EJE(id),
      datos
    );
  }

  /*
    «Convertir la demo»: la MISMA ruta de siempre (`PATCH /tenants/:id` con la marca de prueba quitada), sin
    lógica aparte. Va por `axiosInstance` y no por `apiClient` porque lo que pasó con las estrategias viene en
    `conversion`, en la raíz de la respuesta, y `apiClient` se queda sólo con `data`.
  */
  async convertirDemo(
    id: string,
    plan: { planId: string; endDate: string }
  ): Promise<ResultadoDeLaConversion> {
    // A (2-nov): el plan que elige la plataforma va en el mismo pedido: o se convierte con él o nada.
    const { data } = await axiosInstance.patch(ENDPOINTS.ADMIN_TENANT_BY_ID(id), {
      esDemo: false,
      ...plan,
    });
    return {
      estrategiasPasadasASupervisadas: data.conversion?.estrategiasPasadasASupervisadas ?? [],
      plan: data.conversion?.plan ?? null,
    };
  }

  /** Demo que vence (C): la plataforma le da `dias` más a la prueba (y, si se había desactivado, vuelve a entrar). */
  async extenderPrueba(
    id: string,
    dias: number
  ): Promise<{ venceEl: string; reactivada: boolean }> {
    return apiClient.post<{ venceEl: string; reactivada: boolean }>(
      ENDPOINTS.ADMIN_TENANT_EXTENDER_PRUEBA(id),
      { dias }
    );
  }

  /*
    El alta va por `axiosInstance` y no por `apiClient`: la respuesta trae un
    `warning` en la raíz —"hay que dictarle la contraseña, no se manda correo"— y
    `apiClient` devuelve solo `response.data.data`, con lo que ese aviso se pierde.
  */
  async createTenant(datos: {
    name: string;
    type?: string;
    nit?: string | null;
    email?: string | null;
    phone?: string | null;
    /** Confirma que, aunque se parezca a otra empresa, es una distinta. */
    confirmarParecida?: boolean;
    admin: { email: string; password: string; firstName: string; lastName: string };
  }): Promise<{ tenant: { id: string; name: string }; warning?: string }> {
    const { data } = await axiosInstance.post(ENDPOINTS.ADMIN_TENANT_CREATE, datos);
    return { tenant: data.data.tenant, warning: data.warning };
  }

  /**
   * Clona una empresa entera.
   *
   * Con `ensayo: true` no escribe nada: devuelve cuantas filas por tabla
   * copiaria. Es el primer paso del dialogo, no un extra.
   */
  async clonarEmpresa(
    id: string,
    datos: { name: string; slug: string; incluirUsuarios: boolean; ensayo?: boolean }
  ): Promise<ResumenDelClon> {
    const { data } = await axiosInstance.post(ENDPOINTS.ADMIN_TENANT_CLONE(id), datos);
    return data.data;
  }

  async createUser(datos: {
    tenantId: string;
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    role: string;
  }): Promise<{ user: AdminUser; warning?: string }> {
    const { data } = await axiosInstance.post(ENDPOINTS.ADMIN_USER_CREATE, datos);
    return { user: data.data, warning: data.warning };
  }

  async getTenantDetail(id: string): Promise<any> {
    return apiClient.get(ENDPOINTS.ADMIN_TENANT_BY_ID(id));
  }

  async updateTenant(id: string, data: Record<string, any>): Promise<any> {
    return apiClient.patch(ENDPOINTS.ADMIN_TENANT_BY_ID(id), data);
  }

  /*
    El borrado va por DELETE **con cuerpo**: la confirmación es el slug escrito
    a mano y, cuando la empresa todavía tiene datos, la declaración de que se
    sabe que se borran con ella. axios manda el cuerpo en `config.data`.

    `revisarBorradoDeEmpresa` no borra nada: pide el mismo endpoint en seco y
    devuelve qué tiene la empresa y qué la bloquearía. El diálogo de
    confirmación lo muestra antes de pedir el slug.
  */
  async revisarBorradoDeEmpresa(id: string): Promise<RevisionDeBorrado> {
    return apiClient.delete<RevisionDeBorrado>(ENDPOINTS.ADMIN_TENANT_BY_ID(id), {
      data: { dryRun: true },
    });
  }

  /** Fija el tope de personas de ESTA empresa (excepción, sin tocar el plan). */
  async fijarTopeDePersonas(id: string, maxUsers: number): Promise<void> {
    await apiClient.put(`/api/admin/tenants/${id}/tope-de-personas`, { maxUsers });
  }

  /** Saca la empresa de circulación sin borrar nada (nadie entra, no se scrapea). */
  async archivarTenant(id: string): Promise<void> {
    await apiClient.post(ENDPOINTS.ADMIN_TENANT_ARCHIVAR(id));
  }

  /** Devuelve a quién NO se le reactivó el acceso (y por qué), si alguien quedó afuera. */
  async restaurarTenant(id: string): Promise<{ sinRestaurar: PersonaSinRestaurar[] }> {
    return apiClient.post(ENDPOINTS.ADMIN_TENANT_RESTAURAR(id));
  }

  /** Sin reiniciar nada: qué se borraría de la demo. */
  async revisarReinicioDeDemo(id: string): Promise<RevisionDeReinicio> {
    return apiClient.post<RevisionDeReinicio>(
      ENDPOINTS.ADMIN_TENANT_BY_ID(id) + '/reiniciar-demo',
      {
        dryRun: true,
      }
    );
  }

  /** Reinicia la demo: archiva sus productos y el recorrido empieza de nuevo. `confirmation`: el nombre tal cual. */
  async reiniciarDemo(id: string, confirmation: string): Promise<ResultadoDelReinicio> {
    return apiClient.post<ResultadoDelReinicio>(
      ENDPOINTS.ADMIN_TENANT_BY_ID(id) + '/reiniciar-demo',
      { confirmation }
    );
  }

  async deleteTenant(
    id: string,
    datos: { confirmation: string; acknowledgeData?: boolean }
  ): Promise<{ message?: string }> {
    return apiClient.delete(ENDPOINTS.ADMIN_TENANT_BY_ID(id), { data: datos });
  }

  async getUsers(params?: {
    search?: string;
    role?: string;
    status?: string;
    tenantId?: string;
    page?: number;
    limit?: number;
  }): Promise<{ data: AdminUser[]; pagination: Pagination }> {
    const query = new URLSearchParams();
    if (params?.search) query.set('search', params.search);
    if (params?.role) query.set('role', params.role);
    if (params?.status) query.set('status', params.status);
    if (params?.tenantId) query.set('tenantId', params.tenantId);
    if (params?.page) query.set('page', String(params.page));
    if (params?.limit) query.set('limit', String(params.limit));
    const url = `${ENDPOINTS.ADMIN_USERS}?${query.toString()}`;
    const response = await axiosInstance.get(url);
    return { data: response.data.data, pagination: response.data.pagination };
  }

  async getUserDetail(id: string): Promise<DetalleDeUsuario> {
    return apiClient.get<DetalleDeUsuario>(ENDPOINTS.ADMIN_USER_DETAIL(id));
  }

  async updateUser(id: string, data: Record<string, any>): Promise<AdminUser> {
    return apiClient.patch<AdminUser>(ENDPOINTS.ADMIN_USER_BY_ID(id), data);
  }

  /*
    Soft delete cross-tenant: el backoffice de plataforma puede eliminar a
    cualquier usuario de cualquier empresa. El correo queda ocupado — borrar no
    libera el email —, que es lo que este diálogo avisa antes de confirmar.
  */
  async deleteUser(id: string): Promise<{ message?: string }> {
    return apiClient.delete(ENDPOINTS.ADMIN_USER_BY_ID(id));
  }

  /*
    L5 (plan 335 · 1.2): los accesos de una persona a las empresas. Sólo la plataforma los da,
    cambia, quita o mueve. Quitar apaga el acceso; no borra nada.
  */
  async darAcceso(
    id: string,
    datos: { tenantId: string; role: string; customRoleId?: string | null }
  ): Promise<unknown> {
    return apiClient.post(`${ENDPOINTS.ADMIN_USER_BY_ID(id)}/memberships`, datos);
  }

  async cambiarAcceso(
    id: string,
    tenantId: string,
    cambio: { role?: string; customRoleId?: string | null; isActive?: boolean }
  ): Promise<unknown> {
    return apiClient.patch(`${ENDPOINTS.ADMIN_USER_BY_ID(id)}/memberships/${tenantId}`, cambio);
  }

  /** A3: en qué empresa entra modo soporte, para anunciarlo antes (409 si no se puede entrar). */
  async destinoDeSoporte(id: string): Promise<DestinoDeSoporte> {
    return apiClient.get(`${ENDPOINTS.ADMIN_IMPERSONATE(id)}/destino`);
  }

  /** L5 (1.3): devuelve a una persona eliminada si su correo sigue libre (si no, 409). */
  async restaurarUsuario(id: string): Promise<{
    email: string;
    accesosApagados?: { tenantId: string; empresa: string; rol: string }[];
  }> {
    return apiClient.post(`${ENDPOINTS.ADMIN_USER_BY_ID(id)}/restore`, {});
  }

  /** Lo que hay que saber ANTES de abrirle esa empresa a esta persona (`null` = nada). */
  async avisoDeClave(id: string, tenantId: string): Promise<{ warning: string | null }> {
    return apiClient.get(
      `${ENDPOINTS.ADMIN_USER_BY_ID(id)}/memberships/${tenantId}/aviso-de-clave`
    );
  }

  async quitarAcceso(id: string, tenantId: string): Promise<unknown> {
    return apiClient.delete(`${ENDPOINTS.ADMIN_USER_BY_ID(id)}/memberships/${tenantId}`);
  }

  async moverDeEmpresa(
    id: string,
    datos: { tenantId: string; role: string; customRoleId?: string | null }
  ): Promise<unknown> {
    return apiClient.post(`${ENDPOINTS.ADMIN_USER_BY_ID(id)}/move`, datos);
  }

  /** Qué módulos tiene la empresa, más el catálogo para elegirlos. */
  async getTenantModules(id: string): Promise<ModulosDeEmpresa> {
    return apiClient.get<ModulosDeEmpresa>(ENDPOINTS.ADMIN_TENANT_MODULES(id));
  }

  /**
   * Guarda el techo de la empresa.
   *
   * La lista vacía es válida y lo apaga: la empresa vuelve a ver todo lo que sus
   * roles permitan. El backend devuelve un `message` que lo explica, y conviene
   * mostrarlo tal cual en vez de un "guardado" genérico.
   */
  /*
    Los permisos por rol de otra empresa. Antes esto solo se podía haciendo
    impersonación: entrar como un usuario del cliente, cambiar, y salir.
  */
  async getTenantPermissions(id: string): Promise<PermisosDeEmpresa> {
    return apiClient.get<PermisosDeEmpresa>(ENDPOINTS.ADMIN_TENANT_PERMISSIONS(id));
  }

  async setTenantRolePermissions(
    id: string,
    role: string,
    permissions: string[],
    menuItems: string[]
  ): Promise<{ role: string }> {
    return apiClient.put(ENDPOINTS.ADMIN_TENANT_PERMISSIONS_ROLE(id, role), {
      permissions,
      menuItems,
    });
  }

  async resetTenantRolePermissions(id: string, role: string): Promise<{ role: string }> {
    return apiClient.post(ENDPOINTS.ADMIN_TENANT_PERMISSIONS_RESET(id, role));
  }

  async setTenantModules(id: string, modules: string[]): Promise<{ message?: string }> {
    return apiClient.put(ENDPOINTS.ADMIN_TENANT_MODULES(id), { modules });
  }

  /** Las invitaciones de la demo, la más nueva primero. */
  async getInvitacionesDemo(): Promise<InvitacionDemo[]> {
    return apiClient.get<InvitacionDemo[]>(ENDPOINTS.ADMIN_INVITACIONES_DEMO);
  }

  /** Sin `codigo` el servidor genera uno; con `codigo` usa el que se eligió. */
  async crearInvitacionDemo(datos: {
    nombre: string;
    codigo?: string;
    topeDeAltas?: number | null;
    venceEn?: string | null;
    diasDePrueba?: number;
    socio?: string | null;
  }): Promise<InvitacionDemo> {
    return apiClient.post<InvitacionDemo>(ENDPOINTS.ADMIN_INVITACIONES_DEMO, datos);
  }

  /** Edita o pausa/reactiva (`pausada`). El código no se edita: el enlace ya anda por ahí. */
  async editarInvitacionDemo(
    id: string,
    cambios: {
      nombre?: string;
      topeDeAltas?: number | null;
      venceEn?: string | null;
      diasDePrueba?: number;
      socio?: string | null;
      pausada?: boolean;
    }
  ): Promise<InvitacionDemo> {
    return apiClient.patch<InvitacionDemo>(ENDPOINTS.ADMIN_INVITACION_DEMO(id), cambios);
  }

  async getAltasDeInvitacionDemo(id: string): Promise<AltaDeInvitacionDemo[]> {
    return apiClient.get<AltaDeInvitacionDemo[]>(ENDPOINTS.ADMIN_INVITACION_DEMO_ALTAS(id));
  }
}

export const adminService = new AdminService();
